import type { AgentChangeset, AgentPlannedFiles } from '@/_contract/agentChat';
import type { VisualNode } from '@/adapters/core/_pure/projection';

export type CoreChangeHighlight = { direct: boolean; count: number; planned?: boolean };

export function coreChangeHighlights(nodes: readonly VisualNode[], changes?: AgentChangeset): ReadonlyMap<string, CoreChangeHighlight> {
    if (!changes || (changes.status !== 'ready' && changes.status !== 'recomputing')) return new Map();
    return pathHighlights(nodes, changes.files.map(file => [file.beforePath, file.afterPath]));
}

export function corePlannedHighlights(nodes: readonly VisualNode[], declaration?: AgentPlannedFiles): ReadonlyMap<string, CoreChangeHighlight> {
    if (!declaration?.current || declaration.status !== 'ready') return new Map();
    return pathHighlights(nodes, declaration.files.map(file => [file.path]), true);
}

function pathHighlights(nodes: readonly VisualNode[], files: (string | undefined)[][], planned = false): ReadonlyMap<string, CoreChangeHighlight> {
    const highlights = new Map<string, CoreChangeHighlight>();
    const byPath = new Map(nodes.map(node => [node.path, node]));
    for (const file of files) {
        const targets = new Map<string, boolean>();
        for (const path of new Set(file)) {
            if (!path) continue;
            if (byPath.get(path)?.nodeKind === 'file') {
                targets.set(path, true);
                continue;
            }
            let ancestor = path;
            while (ancestor !== '.') {
                const slash = ancestor.lastIndexOf('/');
                ancestor = slash === -1 ? '.' : ancestor.slice(0, slash);
                if (byPath.get(ancestor)?.nodeKind === 'directory') {
                    targets.set(ancestor, false);
                    break;
                }
            }
        }
        for (const [path, direct] of targets) {
            const prior = highlights.get(path);
            highlights.set(path, { direct: direct || !!prior?.direct, count: (prior?.count ?? 0) + 1, ...(planned ? { planned: true } : {}) });
        }
    }
    return highlights;
}
